import React, { useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useLoader } from '@react-three/fiber';
import { CuboidCollider, RigidBody } from '@react-three/rapier';
import * as THREE from 'three';

// --- 3D Graphics & Physics Concepts ---
//
// **RigidBody:**
// In a physics simulation (like Rapier), a RigidBody is an object that can't be bent or deformed. 
// It has properties like mass, velocity, and friction, and it responds to forces, gravity, and collisions.
// Think of it as the physics representation of a 3D object.
//
// **Mesh:**
// A Mesh is the visual part of a 3D object. It's made up of vertices (points), edges, and faces that define its shape.
// In this file, the <mesh> is the visible cube, while the <RigidBody> is the invisible physics object that controls its movement.
//
// **Collider:**
// A Collider defines the physical shape of an object for collision detection. It's often a simpler shape (like a box or sphere)
// than the visible mesh to make physics calculations faster. Here, a <CuboidCollider> is used for the dice.

// The order of symbols for the dice faces. This should match the texture loading order.
const faceOrder = ['spade', 'club', 'crown', 'flag', 'diamond', 'heart'];

/**
 * A 3D Dice component using React Three Fiber and Rapier physics.
 * This component represents a single die in the game.
 * @param {object} props - The component's props.
 * @param {number[]} props.position - The initial position of the die.
 * @param {React.Ref} forwardedRef - A ref forwarded from the parent component.
 */
export const Dice = forwardRef(({ position }, forwardedRef) => {
  // Ref for the rigid body of the die
  const rigidBodyRef = useRef();
  // Ref for the mesh of the die
  const meshRef = useRef();
  // State to track if the die is sleeping (i.e., has come to a rest)
  const [isSleeping, setIsSleeping] = useState(false);
  
  // URLs for the dice face textures
  const textureUrls = {
    club: './assets/club.png',
    diamond: './assets/diamond.png',
    heart: './assets/heart.png',
    spade: './assets/spade.png',
    flag: './assets/flag.png',
    crown: './assets/crown.png',
  };
  
  // Load the textures for the dice faces
  const textures = useLoader(THREE.TextureLoader, faceOrder.map(symbol => textureUrls[symbol]));
  // Create materials for each face of the die using the loaded textures
  const materials = useMemo(() => textures.map(texture => new THREE.MeshStandardMaterial({ 
    map: texture.clone() 
  })), [textures]);
  
  /**
   * Resets the die position to above the board center before rolling.
   * This prevents dice from accumulating energy when stuck in corners.
   * @param {number} index - The die's index (0-5) used to stagger positions.
   */
  const resetPosition = (index = 0) => {
    if (!rigidBodyRef.current) return;
    
    // Reset velocities to zero before repositioning
    rigidBodyRef.current.setLinvel({ x: 0, y: 0, z: 0 }, true);
    rigidBodyRef.current.setAngvel({ x: 0, y: 0, z: 0 }, true);
    
    // Stagger dice positions slightly so they don't overlap
    const xOffset = (index % 3 - 1) * 1.5;
    const zOffset = (Math.floor(index / 3) - 0.5) * 1.5;
    rigidBodyRef.current.setTranslation(
      { x: xOffset, y: 3 + index * 0.5, z: zOffset },
      true
    );
  };

  /**
   * Rolls the die by resetting its position and applying a random force and torque.
   * @param {number} index - The die's index for staggering start positions.
   */
  const roll = (index = 0) => {
    if (!rigidBodyRef.current) return;
   
    // Reset position to above the board to avoid out-of-bounds issues
    resetPosition(index);

    // Wake the rigid body up to apply forces. A sleeping body is one that the physics engine has stopped calculating
    // to save resources, so it needs to be woken up before new forces can be applied.
    rigidBodyRef.current.wakeUp();
    setIsSleeping(false); // Reset sleeping state
    
    // Reduced force range to prevent dice from flying too far
    const force = 10 + Math.random() * 5;
    const torque = 15 + Math.random() * 5;
   
    // Apply a random impulse. An impulse is a force applied instantly to change an object's linear momentum (its movement in a straight line).
    // This makes the die jump and move.
    rigidBodyRef.current.applyImpulse(
      {
        x: (Math.random() - 0.5) * force,
        y: force,
        z: (Math.random() - 0.5) * force
      },
      true
    );
    
    // Apply a random torque impulse. A torque is a rotational force. This changes the die's angular momentum, making it spin.
    rigidBodyRef.current.applyTorqueImpulse(
      {
        x: (Math.random() - 0.5) * torque,
        y: (Math.random() - 0.5) * torque,
        z: (Math.random() - 0.5) * torque,
      },
      true
    );
  };
  
  /**
   * Returns the mesh of the die.
   * @returns {THREE.Mesh} The die's mesh.
   */
  const getMesh = () => {
    return meshRef.current;
  };
  
  /**
   * Returns the rotation of the die's rigid body.
   * @returns {THREE.Quaternion} The die's rotation.
   */
  const getRotation = () => {
    if (!rigidBodyRef.current) return null;
    return rigidBodyRef.current.rotation();
  };
  
  // Expose methods and properties to the parent component through the ref
  useImperativeHandle(forwardedRef, () => ({
    roll,
    resetPosition,
    get isSleeping() { return isSleeping; },
    get mesh() { return getMesh(); },
    get rotation() { return getRotation(); },
    rigidBodyRef
  }));
  
  return (
    // This is the physics object. It's not visible but it controls the position and rotation of the mesh inside it.
    <RigidBody
      ref={rigidBodyRef}
      position={position}
      colliders={false} // We are defining a custom collider below
      linearDamping={0.6} // Simulates air resistance on movement
      angularDamping={0.6} // Simulates air resistance on rotation
      onSleep={() => setIsSleeping(true)} // Callback when the die stops moving
      onWake={() => setIsSleeping(false)} // Callback when the die starts moving
    >
      {/* This is the visible 3D cube. Its position is controlled by the parent RigidBody. */}
      <mesh ref={meshRef} material={materials} castShadow>
        <boxGeometry args={[1, 1, 1]} />
      </mesh>
      {/* This is the collision shape for the physics engine. It's a simple cube. */}
      <CuboidCollider args={[0.5, 0.5, 0.5]} />
    </RigidBody>
  );
});